import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import React, { createContext, useContext, useEffect, useState } from 'react'

import { auth } from 'constants/fireabaseConfig'
import { protectedRoutes } from 'constants/routes'

import { type Groups, type ResponseResult } from 'api-client'
import { useFcmTopic } from 'hooks/useFcmTopic'
import { useLoginUserInfo } from 'hooks/useLoginUserInfo'
// import { useOrderAlert } from 'hooks/useOrderAlert'
import { useTypecode } from 'hooks/useTypecode'

import type { User } from 'firebase/auth'
import type { ErrorResponse, LoginUserWithMfa } from 'types/types'

interface AuthContextProps {
  user: User | null
  typeCode: Groups[]

  loginUserInfo: LoginUserWithMfa | undefined
  setLoginUserInfo: Dispatch<SetStateAction<LoginUserWithMfa | undefined>>
  loginUserInfoResult: ResponseResult | undefined
  setLoginUserInfoResult: Dispatch<SetStateAction<ResponseResult | undefined>>

  isLoginLoading: boolean
  setIsLoginLoading: Dispatch<SetStateAction<boolean>>
  unsubscribeFromCompanyTopic: () => Promise<void>
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  typeCode: [],

  loginUserInfo: undefined,
  setLoginUserInfo: () => {},
  loginUserInfoResult: undefined,
  setLoginUserInfoResult: () => {},

  isLoginLoading: false,
  setIsLoginLoading: () => {},
  unsubscribeFromCompanyTopic: async () => {},
})

export function useAuthContext() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: PropsWithChildren) {
  const {
    loginUserInfo,
    setLoginUserInfo,
    fetchLoginUserInfo,
    result: loginUserInfoResult,
    setResult: setLoginUserInfoResult,
    isLoading: isLoginUserInfoLoading,
  } = useLoginUserInfo()
  const { typeCode, fetchTypeCode, isLoading: isTypeCodeLoading } = useTypecode()
  // const { fetchAlertSummary } = useOrderAlert()
  const { subscribeToCompanyTopic, unsubscribeFromCompanyTopic } = useFcmTopic((payload) => {
    if (Notification.permission !== 'granted' || !payload.notification) return

    const title = payload.notification.title ?? '処理完了通知'
    // event_id は送信ごとに一意で data 内にあり全タブで同一。
    // 同じ tag → 単一 SW registration 上で 1 件に統合（複数タブの重複表示防止）。
    const tag = payload.data?.event_id
    const options: NotificationOptions = {
      body: payload.notification.body ?? '',
      icon: '/favicon.ico',
      tag,
    }

    navigator.serviceWorker?.getRegistrations().then((regs) => {
      if (regs && regs.length > 0) {
        regs[0].showNotification(title, options)
      } else {
        const notification = new Notification(title, options)
        notification.onclick = () => {
          window.focus()
        }
      }
    })
  })

  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const [isLoginLoading, setIsLoginLoading] = useState(false)

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    user,
    typeCode,

    loginUserInfo,
    setLoginUserInfo,
    loginUserInfoResult,
    setLoginUserInfoResult,

    isLoginLoading,
    setIsLoginLoading,
    unsubscribeFromCompanyTopic,
  }

  useEffect(() => {
    // サイン状態を監視
    const unsubscribed = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        fetchLoginUserInfo()
          .then(async (response) => {
            // FCM トピックにサブスクライブ（ログイン後）
            if (response?.data?.companyId) {
              subscribeToCompanyTopic(response.data.companyId).catch(() => undefined)
            }
          })
          .catch(({ response }: ErrorResponse) => {
            setLoginUserInfoResult({
              status: 'error',
              message:
                response && response.status === 401
                  ? 'メールアドレスまたはパスワードが異なります。'
                  : 'ログインユーザ情報取得でエラーが発生しました。',
              systemDate: null,
              pageNo: 0,
              systemInfo: null,
            })
          })
        fetchTypeCode()
        // fetchAlertSummary({ enabled: false }).catch(() => undefined)
        setUser(authUser)
        setIsLoading(false)
      } else {
        setIsLoading(false)
        // サインインしていない場合は、ログイン画面に遷移する
        if (!isLoading) {
          window.location.href = `/${protectedRoutes.Login.path}?tenantId=${user?.tenantId}`
        }
      }
    })
    return () => {
      unsubscribed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isLoading || isTypeCodeLoading || isLoginUserInfoLoading || isLoginLoading ? (
    <div />
  ) : (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  )
}
